/* eslint-disable @typescript-eslint/no-unused-vars */
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router';
import {
  Button,
  ButtonContainer,
  ContentSection,
  Fieldset,
  Heading,
  IconName,
  InlineGroup,
  Layout,
  PageWrap,
  SectionHeading,
} from '@vwfs-bronson/bronson-react';
import { Page } from '../../../..';
import { PaginationComponent } from '../../../../components/Pagination';
import { DealerDashboardSubpage, QuoteFlow, QuoteStatusId } from '../../../../resources/base/models/enums';
import { routes } from '../../../../routing/routes';
import {
  cancelQuote,
  getQuote,
  getQuotes,
  updateQuoteSalesPerson,
} from '../../../../services/api/miles/miles-services';
import { formatDateWithPoints, formatDecimalComma, formatPrice, isoDate } from '../../../../services/common/format';
import { assertDefined, getDisplayedManufacturerLabel } from '../../../../services/common/miscUtils';
import { staticSelector, store } from '../../../../services/redux';
import { brokerSelectors } from '../../../../services/redux/features/broker.slice';
import { setIsCarUsed, setSelectedVehicleType } from '../../../../services/redux/features/custom.slice';
import { setCustomerInitialState } from '../../../../services/redux/features/customer.slice';
import {
  dashboardSelectors,
  resetDashboardSearchForm,
  setDashboardSearchFormWithSelections,
  setSelectedComparisonOfferIds,
  setSubPageLabelStatusIds,
} from '../../../../services/redux/features/dashboard.slice';
import { loadingActions } from '../../../../services/redux/features/loading.slice';
import { isQuoteUnlockable, setQuoteData, setQuoteFlow } from '../../../../services/redux/features/quote.slice';
import { getInitialCustomerStateFromQuote } from '../../../../services/redux/reduxUtils';
import { useNavigate } from '../../../../services/routing/navigate';
import { calculateQuoteFinancedAmount } from '../../../FormPage/Step3/calculations';
import { getVehicleType, handleCopyQuote, isCarUsedFromQuote, preventCopyQuoteStatuses } from '../../../utils';
import { ButtonAction, Buttons } from '../../constants/ButtonAction';
import { paginationInitialState } from '../../constants/DefaultPagination';
import {
  isButtonDisabledByStatus,
  offerStatusIdOptionsList,
  OfferStatusIdType,
  removedAnnuleInitialQuotesSubpage,
} from '../../constants/StatusMap';
import { DashboardActionPopup, DashboardPopupTransfer } from '../../popups/DashboardPopups';
import { DashBoardSearchSection } from '../components/DashBoardSearchSection';
import { DashboardSubpagesTabs } from '../components/DashboardSubpagesTabs';
import { DashBoardTable } from '../components/DashBoardTable';
import { handleSelectionAfterTableUpdate } from '../utils/dashboardSubpageTableSelection';
import { initializeSalesPersonDropdown } from '../utils/initializeSalesPersonDropdown';
import { mapResponsePagination } from '../utils/mapResponsePagination';
import { checkIfButtonShouldBeDisabled as isButtonDisabledByMultiSelection } from '../utils/multiselectionButtonCheck';
import { isSelectedOfferEligible, MAX_COMPARE_SELECTIONS, MIN_COMPARE_SELECTIONS } from '../utils/quoteComparison';
import { SearchedValues } from '../utils/searchedValues.types';
import { setFinancialProductList } from '../utils/setFinancialProductDropdown';

const searchFields = ['salesPerson', 'creationDate', 'statusId', 'financialProduct'];

const tableHeaders = [
  'offerReference',
  'tradingName',
  'siren',
  'usage',
  'manufacturer',
  'modelDescription',
  'productName',
  'eligibility',
  'grossLoanAmount',
  'durationMileage',
  'spreadLeasePrice',
  'statusId',
  'offreplus',
  'creationDate',
];

const buttonList = [
  { value: 'Copier', label: 'Copier', enableOnMultiSelect: false },
  { value: 'Annuler', label: 'Annuler', enableOnMultiSelect: false, disabledOnStatuses: ['100051'] },
  { value: 'Transfert', label: 'Transfert', enableOnMultiSelect: true, disabledOnStatuses: ['100051'] },
  { value: 'Documents', label: 'Documents', enableOnMultiSelect: false },
  // PC-4402 Temporary hidden
  // { value: 'Comparer', label: 'Comparer', enableOnMultiSelect: true, disabledOnStatuses: ['100051'] },
] as Buttons;

export const DashboardSubpageQuotes: FunctionComponent = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedOfferIds, setSelectedOfferIds] = useState<string[]>([]);
  const selectedComparisonOfferIds = useSelector(dashboardSelectors.selectSelectedComparisonOfferIds);
  const [isComparisonMode] = useState(() => location.state?.comparisonMode ?? false);
  const [allowQuoteComparison, setAllowQuoteComparison] = useState<boolean>(false);
  const [showCopyPopup, setShowCopyPopup] = useState(false);
  const [showModifyPopup, setShowModifyPopup] = useState(false);
  const [salesPersons, setSalesPersons] = useState<Array<{ value: string; label: string }>>([] as any);
  const [chosenSalesPerson, setChosenSalesPerson] = useState('');
  const [showTransfertPopup, setShowTransfertPopup] = useState(false);
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [tableData, setTableData] = useState<any[]>([]);
  const [pagination, setPagination] = useState(paginationInitialState);
  const [searchedValues, setSearchedValues] = useState<SearchedValues>({});
  const [savedInputfieldValues, setSavedInputfieldValues] = useState<SearchedValues>({});
  const [contractsTotalCount, setContractsTotalCount] = useState<number>(0);
  const [financialProductDropdownList, setFinancialProductDropdownList] = useState<
    ReadonlyArray<{ value: string; label: string }>
  >([]);
  const [allQuotesData, setAllQuotesData] = useState<QuoteSchema.Schemas.QuoteResponse[]>();
  const [modifyPopupText, setModifyPopupText] = useState<string>('');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tableRef = useRef<any>(null);

  const milesSalesPersonId = useSelector(brokerSelectors.milesSalesPersonId);
  const subPageLabelStatusIds: OfferStatusIdType[] | undefined = useSelector(
    dashboardSelectors.selectSubPageLabelStatusIds
  );

  useEffect(() => {
    if (searchParams.has('offerIds')) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const overallFilterStatusIds: OfferStatusIdType[] = subPageLabelStatusIds || removedAnnuleInitialQuotesSubpage;
  const searchForm = useSelector(dashboardSelectors.selectDashboardSearchFormWithSelections);
  const hasStoredSearch = !!searchForm && Object.keys(searchForm).length > 0;
  const isAddToCompareButtonDisabled =
    !allowQuoteComparison || selectedComparisonOfferIds.length >= MAX_COMPARE_SELECTIONS;

  const handleButtonClick = (action: ButtonAction) => {
    switch (action) {
      case 'Copier':
        setShowCopyPopup(true);
        break;
      case 'Annuler':
        setShowCancelPopup(true);
        break;
      case 'Transfert':
        setShowTransfertPopup(true);
        break;
      case 'Documents':
        dispatch(
          setDashboardSearchFormWithSelections({
            ...savedInputfieldValues,
            selectedCheckboxes: selectedOfferIds,
            currentPage: pagination?.currentPage,
          })
        );
        navigate.url(`${routes.dashboardQuotesDocuments.replace(':id', selectedOfferIds[0])}`);
        break;
      case 'Comparer':
        dispatch(
          setDashboardSearchFormWithSelections({
            ...savedInputfieldValues,
            selectedCheckboxes: selectedOfferIds,
            currentPage: pagination?.currentPage,
          })
        );
        navigate.dashboardCompare(`?offerIds=${selectedComparisonOfferIds}`);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!tableData.length) return;
    if (!tableRef.current) return;

    tableRef.current.setSelections(selectedComparisonOfferIds);
    setSelectedOfferIds(selectedComparisonOfferIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData]);

  useEffect(() => {
    if (!allQuotesData) {
      setAllowQuoteComparison(false);
      return;
    }

    if (selectedOfferIds.length === 0) {
      setAllowQuoteComparison(false);
      return;
    }

    const lastSelectedItem = selectedOfferIds[selectedOfferIds.length - 1];

    const isItemEligible =
      isComparisonMode && selectedComparisonOfferIds.includes(lastSelectedItem)
        ? true
        : isSelectedOfferEligible(allQuotesData, lastSelectedItem);

    setAllowQuoteComparison(isItemEligible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOfferIds, selectedComparisonOfferIds, allQuotesData]);

  const mapResponseQuote = async (quote: any): Promise<any> => {
    const { customerType } = quote.customerData;
    const isPrivate = customerType === 'INDIVIDUAL';
    const statusId = quote?.financialProduct?.offers[0].statusId;
    const { description } = quote.vehicleData.model;
    const financedAmount = calculateQuoteFinancedAmount(quote);
    const manufacturer = quote?.vehicleData?.model?.manufacturer;

    return {
      offerId: quote?.financialProduct?.offers?.[0]?.offerId,
      offerReference: quote?.financialProduct?.offers?.[0]?.offerReference,
      tradingName: quote?.customerData?.tradingName || quote?.customerData?.companyData?.name,
      siren: quote?.customerData?.companyData?.companyRegistrationNumber,
      usage: isPrivate
        ? t('dashboard:table:body:privateCustomerUsage')
        : t('dashboard:table:body:businessCustomerUsage'),
      manufacturer: getDisplayedManufacturerLabel(manufacturer),
      modelDescription: description.substring(description.indexOf(' ') + 1),
      productName: quote?.financialProduct?.offers[0]?.productDescription,
      eligibility: formatDecimalComma(quote?.financialProduct?.offers[0]?.calculation.eligibility),
      grossLoanAmount: formatPrice(financedAmount),
      durationMileage: `${quote?.financialProduct?.offers[0]?.calculation.duration || ''}/${quote?.financialProduct?.offers[0]?.calculation.contractMileage || ''}`,
      spreadLeasePrice: formatPrice(
        quote?.financialProduct?.offers[0]?.calculation.spreadLeasePrice?.grossAmount || ''
      ),
      statusId: offerStatusIdOptionsList[statusId]?.label,
      creationDate: formatDateWithPoints(quote?.financialProduct?.offers[0]?.createdAt),
      offreplus: quote?.financialProduct?.offers?.[0]?.offreplus,
    };
  };

  const mapQuotes = (quotes: any[]): Promise<any[]> => {
    if (!quotes?.length) {
      return Promise.resolve([]);
    }
    return Promise.all(quotes?.map((quote: any) => mapResponseQuote(quote)));
  };

  const resetTableSelections = () => {
    if (tableRef.current) {
      tableRef.current.clearSelections();
    }
  };

  const doGetQuotes = async (data: any = {}) => {
    const currentCompany = staticSelector(brokerSelectors.currentCompany);
    const milesCompanyIds = [assertDefined(currentCompany?.milesCompanyId)];

    const requestData = data?.statusId
      ? data
      : {
          ...data,
          statusId: overallFilterStatusIds,
        };
    const response = await getQuotes({
      milesCompanyIds,
      ...(searchForm.currentPage ? { page: searchForm.currentPage } : {}),
      ...requestData,
    });
    setContractsTotalCount(response?.totalCount ?? 0);
    const mappedQuotes = await mapQuotes(response?.quotes ?? []);
    setTableData(mappedQuotes);
    setAllQuotesData(response?.quotes);
    setSelectedOfferIds((prev) => handleSelectionAfterTableUpdate(prev, mappedQuotes, resetTableSelections));
    setPagination(mapResponsePagination(response));
  };

  const modifyRow = async () => {
    try {
      store.dispatch(loadingActions.setLoading(true));
      const quoteData = await getQuote(selectedOfferIds[0]);
      store.dispatch(setIsCarUsed(isCarUsedFromQuote(quoteData)));
      const customerInitialState = await getInitialCustomerStateFromQuote(quoteData);
      store.dispatch(setCustomerInitialState(customerInitialState));
      store.dispatch(setSelectedVehicleType(getVehicleType(quoteData)));
      store.dispatch(setQuoteData(quoteData));
      store.dispatch(setQuoteFlow(QuoteFlow.MODIFY));
      store.dispatch(loadingActions.setLoading(false));
      navigate.form(`/3`);
    } catch {
      store.dispatch(loadingActions.setLoading(false));
      navigate.error();
    }
  };

  const copySelectedQuote = async () => {
    await handleCopyQuote(selectedOfferIds[0], navigate, 3);
  };

  const cancelRow = async () => {
    try {
      store.dispatch(loadingActions.setLoading(true));
      await cancelQuote(selectedOfferIds[0]);
      await doGetQuotes({
        page: pagination?.currentPage,
        pageSize: pagination?.pageSize,
        ...searchedValues,
      });
      store.dispatch(loadingActions.setLoading(false));
    } catch {
      store.dispatch(loadingActions.setLoading(false));
      navigate.error();
    }
  };

  const handleConfirm = async (action: ButtonAction) => {
    switch (action) {
      case 'Copier':
        setShowCopyPopup(false);
        copySelectedQuote();
        break;
      case 'Modifier':
        setShowModifyPopup(false);
        modifyRow();
        break;
      case 'Annuler':
        cancelRow();
        setShowCancelPopup(false);
        break;
      case 'Transfert':
        try {
          store.dispatch(loadingActions.setLoading(true));
          await Promise.all(selectedOfferIds.map((item) => updateQuoteSalesPerson(chosenSalesPerson, item)));
        } catch (err) {
          navigate.error();
        } finally {
          resetTableSelections();
          setSelectedOfferIds([]);
          setShowTransfertPopup(false);
          store.dispatch(loadingActions.setLoading(false));
        }
        break;
      default:
        break;
    }
  };

  const searchFieldsMap = (values: Partial<SearchedValues>): SearchedValues => ({
    ...(values?.searchQuery && { searchQuery: values?.searchQuery }),
    ...(values?.creationDate &&
      !values?.creationDate.includes('au') && {
        creationDate: isoDate(values?.creationDate),
      }),
    ...(values?.creationDate &&
      values?.creationDate.includes('au') && {
        creationDateFrom: isoDate(values.creationDate.split(' au ')[0]),
        creationDateTo: isoDate(values.creationDate.split(' au ')[1]),
      }),
    ...(values?.salesPerson && { salesPerson: values?.salesPerson }),
    ...(values?.statusId && { statusId: values?.statusId }),
    ...(values?.financialProduct && { financialProduct: values?.financialProduct }),
    pageSize: pagination?.pageSize,
  });

  const searchQuotes = async (values: Partial<SearchedValues>) => {
    dispatch(setSubPageLabelStatusIds(undefined));
    setSavedInputfieldValues(values);
    const newSearchedValues: SearchedValues = searchFieldsMap(values);
    setSearchedValues(newSearchedValues);
    await doGetQuotes({
      ...newSearchedValues,
      statusId: newSearchedValues.statusId ?? overallFilterStatusIds,
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      if (hasStoredSearch) {
        dispatch(resetDashboardSearchForm());
        searchQuotes(searchForm);
      } else if (milesSalesPersonId) {
        const defaultFilter = { salesPerson: milesSalesPersonId };
        setSearchedValues(defaultFilter);
        await doGetQuotes(defaultFilter);
      } else {
        await doGetQuotes();
      }

      await initializeSalesPersonDropdown(setSalesPersons);
      setFinancialProductDropdownList((await setFinancialProductList()) || []);
    };

    fetchData();
    return () => {
      dispatch(setSubPageLabelStatusIds(undefined));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paginationOnChange = async (page: Page) => {
    await doGetQuotes({ page, ...searchedValues });
    resetTableSelections();
    setSelectedOfferIds([]);
  };

  const onTableSelection = (selectedIds: string[]) => {
    setSelectedOfferIds(selectedIds);
  };

  const addToCompareSelection = () => {
    if (selectedOfferIds.length === 0) return;
    const lastSelectedItem = selectedOfferIds[selectedOfferIds.length - 1];

    dispatch(
      setSelectedComparisonOfferIds(
        selectedComparisonOfferIds.includes(lastSelectedItem)
          ? selectedComparisonOfferIds
          : [...selectedComparisonOfferIds, lastSelectedItem]
      )
    );
  };

  const getModifyPopupText = (quote: QuoteSchema.Schemas.QuoteResponse): string => {
    const statusId = quote?.financialProduct?.offers?.[0]?.statusId;

    if (statusId === QuoteStatusId.Expired) {
      return t('dashboard:popup:inactiveOfferPopup');
    }

    if (statusId === QuoteStatusId.Canceled || statusId === QuoteStatusId['Web Canceled']) {
      return t('dashboard:popup:modifyCancelledPopup');
    }
    return isQuoteUnlockable(quote)
      ? t('dashboard:popup:modifyQuotePopup')
      : t('dashboard:popup:modifyReadOnlyQuotePopup');
  };

  const onRowDbClick = (selectedId: string) => {
    setSelectedOfferIds([selectedId]);
    const quote = allQuotesData?.find((quote) =>
      quote?.financialProduct?.offers?.some((offer) => offer.offerId === selectedId)
    );
    setModifyPopupText(getModifyPopupText(quote! as QuoteSchema.Schemas.QuoteResponse));
    setShowModifyPopup(true);
  };

  const isCopyButtonDisabledByStatus = (label: ButtonAction) => {
    if (label !== 'Copier') return false;

    const statusId = allQuotesData?.find((quote) =>
      selectedOfferIds.includes(quote?.financialProduct?.offers?.[0]?.offerId ?? '')
    )?.financialProduct?.offers?.[0]?.statusId as OfferStatusIdType;

    return preventCopyQuoteStatuses.includes(statusId);
  };

  const isCompareButtonDisabled = (label: ButtonAction) => {
    if (label !== 'Comparer') return false;
    return (
      !allowQuoteComparison ||
      selectedComparisonOfferIds.length < MIN_COMPARE_SELECTIONS ||
      selectedComparisonOfferIds.length > MAX_COMPARE_SELECTIONS
    );
  };

  const selectedStatusIds = (allQuotesData ?? [])
    .filter((quote) => selectedOfferIds.includes(quote?.financialProduct?.offers?.[0]?.offerId ?? ''))
    .map((quote) => quote?.financialProduct?.offers?.[0]?.statusId as OfferStatusIdType | undefined);

  return (
    <>
      <ContentSection pageWrap className="u-pt-none">
        <DashboardSubpagesTabs />
      </ContentSection>

      <ContentSection className="u-pv-small" pageWrap>
        <Layout>
          <Layout.Item default="5/6" s="1/1" className="u-mt-none">
            <Heading level={2}>{t('dashboard:search:header')}</Heading>
          </Layout.Item>
        </Layout>
      </ContentSection>

      <PageWrap>
        <Layout>
          <Layout.Item default="1/1" s="1/1">
            <DashBoardSearchSection
              fields={searchFields}
              searchQuotes={searchQuotes}
              searchQuery
              subpage={DealerDashboardSubpage.Summary}
              salesPersonDropdown={salesPersons}
              searchFieldFinancialProductDropdownValues={financialProductDropdownList}
              defaultSalesPerson={milesSalesPersonId}
            />
          </Layout.Item>
        </Layout>
      </PageWrap>

      <ContentSection pageWrap>
        <Layout.Item className="u-text-center u-mt-none">
          <InlineGroup>
            <div className="o-inline-group__item">
              <SectionHeading level={3}>
                {t('dashboard:search:results', {
                  numResults: contractsTotalCount,
                })}
              </SectionHeading>
            </div>

            {/* 
            PC-4402 Temporary hidden
            <div className="o-inline-group__item">
              <Button
                round
                link
                icon={IconName.ZOOM_IN}
                onClick={addToCompareSelection}
                // Disable if compare not allowed or limit (3) reached
                disabled={isAddToCompareButtonDisabled}
              >
                {t('dashboard:buttons:addToCompare')}{' '}
                {`(${selectedComparisonOfferIds.length}/${MAX_COMPARE_SELECTIONS})`}
              </Button>
            </div> */}
          </InlineGroup>

          <DashBoardTable
            ref={tableRef}
            headers={tableHeaders}
            onSelection={onTableSelection}
            values={tableData}
            onDoubleClick={onRowDbClick}
          />
          <Layout center>
            <PaginationComponent pagination={pagination} onChange={paginationOnChange} />
          </Layout>
        </Layout.Item>
      </ContentSection>

      <Fieldset.Row>
        <ButtonContainer center>
          <Layout.Item className="u-text-center">
            {buttonList?.map(({ label, value, enableOnMultiSelect, disabledOnStatuses }) => (
              <Button
                key={`${label}`}
                value={`${value}`}
                type="button"
                className="u-m-small"
                secondary
                onClick={() => handleButtonClick(label)}
                disabled={
                  isButtonDisabledByMultiSelection(selectedOfferIds, enableOnMultiSelect) ||
                  isCopyButtonDisabledByStatus(label) ||
                  isCompareButtonDisabled(label) ||
                  isButtonDisabledByStatus(disabledOnStatuses, selectedStatusIds)
                }
              >
                {label}
              </Button>
            ))}
            {showModifyPopup && (
              <DashboardActionPopup
                handleClose={() => setShowModifyPopup(false)}
                onConfirm={() => handleConfirm('Modifier')}
                text={modifyPopupText}
              />
            )}
            {showCopyPopup && (
              <DashboardActionPopup
                handleClose={() => setShowCopyPopup(false)}
                onConfirm={() => handleConfirm('Copier')}
                text={t('dashboard:popup:copyTextTitle')}
              />
            )}
            {showCancelPopup && (
              <DashboardActionPopup
                handleClose={() => setShowCancelPopup(false)}
                onConfirm={() => handleConfirm('Annuler')}
                text={t('dashboard:popup:cancelTextTitle')}
              />
            )}

            {showTransfertPopup && (
              <DashboardPopupTransfer
                handleClose={() => setShowTransfertPopup(false)}
                onConfirm={() => handleConfirm('Transfert')}
                chooseSalesPerson={setChosenSalesPerson}
                salesPersons={salesPersons}
                quoteIds={selectedOfferIds}
                subpage={DealerDashboardSubpage.Summary}
              />
            )}
          </Layout.Item>
        </ButtonContainer>
      </Fieldset.Row>
    </>
  );
};
